<script setup lang="ts">
//#region imports
import { cloneDeepWith, get, isEqual } from 'lodash-es';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useApi } from '@/api';
import PermissionTree from '@/components/PermissionTree.vue';
import PermissionTreeCommon from '@/components/PermissionTreeCommon.vue';
import PermissionSwitchControl from '@/views/Teams/components/PermissionSwitchControl.vue';
import { dependsOnConfig, requiresForConfig, resetFieldsConfig } from '@/configs/permissions';
import {
  applyPermissionsSnapshot,
  getPermissionsObject,
  getPermissionsSnapshot,
  sortPermissionsSnapshot,
} from '@/helpers/permissions';
import { useTypedStore } from '@/store';
import { isErrorResponse, type FormErrors } from '@/types/core';
import type {
  GetAdminsResponse,
  GetAffiliateOffersCatalogCategoriesResponse,
  GetCustomerCurrenciesResponse,
  PostTeamsByIdPermissionsData,
  GetTeamsByIdPermissionsResponse,
  GetTeamsResponse,
} from '@/types/openapi-ts';
import type { AdminPermissionKey } from '@/types/teams';
//#endregion

//#region types
type Admin = GetAdminsResponse['payload'][number];
type Category = GetAffiliateOffersCatalogCategoriesResponse['payload'][number];
type Currency = GetCustomerCurrenciesResponse['payload'][number];
type Team = GetTeamsResponse['payload'][number];
type TeamPermissions = GetTeamsByIdPermissionsResponse['payload'];
type TeamPermissionsState = Partial<TeamPermissions>;
type CrmPermission = 'all' | 'teammates' | 'own' | 'deny';

interface Props {
  teamUsers?: Admin[];
  permissions?: Partial<TeamPermissions>;
  teamInfo?: Partial<Team>;
}

interface Emits {
  'update-permissions': [data: TeamPermissions];
}

interface Option<T extends string = string> {
  label: string;
  value: T;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  teamUsers: () => [],
  permissions: () => ({
    affiliates_types_edit: [],
    affiliates_types_view: [],
    players_available_property_groups: [],
    media_buying_reports_affiliates_types_view: [],
    reports_affiliates_types: [],
    media_buying_payments_affiliates_types_view: [],
    media_buying_payments_affiliates_types_review: [],
    media_buying_posts_affiliates_types_view: [],
    media_buying_posts_affiliates_types_review: [],
    media_buying_posts_statuses_visible: [],
    reports_view_affiliates_visibility_teammates_id: [],
    offers_link_to_catalog_categories_allowed_mode: 'allow_all',
    offers_link_to_catalog_categories_allowed_list: [],
  }),
  teamInfo: () => ({}),
});
const emit = defineEmits<Emits>();
defineOptions({
  name: 'Permissions',
});
//#endregion

//#region const
const crmWeight = {
  all: 3,
  teammates: 2,
  own: 1,
  deny: 0,
} satisfies Record<CrmPermission, number>;
const crmAllowOptions = {
  all: ['all', 'teammates', 'own', 'deny'],
  teammates: ['teammates', 'own', 'deny'],
  own: ['own', 'deny'],
  deny: ['deny'],
} satisfies Record<CrmPermission, CrmPermission[]>;
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isLoading = ref(false);
const isCategoriesLoading = ref(false);
const pvPermissions = ref<TeamPermissionsState>({});
const users = ref<Admin[]>([]);
const currencyOptions = ref<Currency[]>([]);
const dataCategories = ref<Category[]>([]);
const errors = ref<FormErrors>({});
const permissionSnapshots = ref<Record<string, Partial<TeamPermissionsState>>>({});
const activePermissions = ref(['reports']);
//#endregion

//#region computed
const isEdited = computed(() => !isEqual(props.permissions, pvPermissions.value));
const countries = computed(() => store.getters['misc/countries']);
const paymentReviewTeammateIds = computed({
  get: () => (
    pvPermissions.value.media_buying_payments_review_affiliates_visibility_teammates_id
  ),
  set: (value) => {
    pvPermissions.value.media_buying_payments_review_affiliates_visibility_teammates_id = value;
  },
});
const activeTeamId = computed(() => {
  const id = route.params.id;
  return Array.isArray(id) ? (id[0] ?? '') : (id ?? '');
});
const statusList = computed<Option[]>(() => [
  {
    label: t('permissions.on_review'),
    value: 'on_review',
  },
  {
    label: t('permissions.approved'),
    value: 'approved',
  },
  {
    label: t('permissions.for_rework'),
    value: 'for_rework',
  },
  {
    label: t('permissions.declined'),
    value: 'declined',
  },
]);
const partnersTypeList = computed<Option[]>(() => [
  {
    label: t('permissions.independent'),
    value: 'independent',
  },
  {
    label: t('permissions.other_teams_affiliates'),
    value: 'other_teams_affiliates',
  },
  {
    label: t('permissions.own_affiliates'),
    value: 'own_affiliates',
  },
]);
const crmRadioList = computed<Option<CrmPermission>[]>(() => [
  {
    label: t('permissions.all'),
    value: 'all',
  },
  {
    label: t('permissions.own'),
    value: 'own',
  },
  {
    label: t('permissions.teammates'),
    value: 'teammates',
  },
  {
    label: t('permissions.deny'),
    value: 'deny',
  },
]);
//#endregion

//#region methods
function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = cloneDeepWith(error.data?.errors) ?? {};
}

function getError(field: string, index = 0) {
  return get(errors.value, [field, index, 'message'], '');
}

function getUsers() {
  isLoading.value = true;
  errors.value = {};
  api.getAdmins({ team_id: activeTeamId.value }).then((response) => {
    users.value = response.data.payload;
  }).catch(setErrors).finally(() => {
    isLoading.value = false;
  });
}

function getCurrencies() {
  api.getMiscCustomerCurrencies().then((response) => {
    currencyOptions.value = response.data.payload;
  });
}

function checkWeight(currentOption: AdminPermissionKey, childOption: AdminPermissionKey) {
  const currentOptionValue = pvPermissions.value[currentOption];
  const childOptionValue = pvPermissions.value[childOption];

  if (!isCrmPermission(currentOptionValue) || !isCrmPermission(childOptionValue)) {
    return;
  }

  if (crmWeight[childOptionValue] > crmWeight[currentOptionValue]) {
    pvPermissions.value[childOption] = currentOptionValue;
  }
}

function isCrmPermission(value: unknown): value is CrmPermission {
  return typeof value === 'string' && value in crmWeight;
}

function isDisabledOption(property: AdminPermissionKey, option: CrmPermission) {
  const value = pvPermissions.value[property];
  return isCrmPermission(value)
    ? !crmAllowOptions[value].some((allowedOption) => allowedOption === option)
    : true;
}

async function postTeamPermissions() {
  errors.value = {};

  try {
    const response = await api.postTeamPermissions(
      activeTeamId.value,
      pvPermissions.value as PostTeamsByIdPermissionsData['body'],
    );
    emit('update-permissions', response.data.payload);
  } catch (error: unknown) {
    setErrors(error);
  }
}

function checkGeoPermission(permission?: string) {
  errors.value = {};
  if (permission === 'allow_all' || permission === 'deny_all') {
    pvPermissions.value.countries_allowed_list = [];
  }
}

function onOffersCatalogModeChange(value: string | null) {
  if (value === 'allow_all') {
    pvPermissions.value.offers_link_to_catalog_categories_allowed_list = [];
  }
}

function onOffersCatalogIdsChange(value: unknown[] | null) {
  if (
    pvPermissions.value.offers_link_to_catalog_categories_allowed_mode === 'allow_all'
    && value
    && value.length > 0
  ) {
    pvPermissions.value.offers_link_to_catalog_categories_allowed_mode = 'allow_selected';
  }
}

function getCategories() {
  isCategoriesLoading.value = true;
  api.getAffiliateOffersCatalogCategories().then((response) => {
    dataCategories.value = response.data.payload;
  }).finally(() => {
    isCategoriesLoading.value = false;
  });
}

function cancelEdit() {
  errors.value = {};
  pvPermissions.value = cloneDeepWith(props.permissions);
}

function permissionIsDeny(permission: AdminPermissionKey) {
  return pvPermissions.value[permission] === 'deny';
}

function restoreClearedCheckboxFields(snapshot?: Partial<TeamPermissionsState>) {
  if (!snapshot) return;

  Object.entries(snapshot).forEach(([key, value]) => {
    const permission = key as AdminPermissionKey;

    if (
      Array.isArray(value)
      && value.length
      && !pvPermissions.value[permission]?.length
    ) {
      pvPermissions.value[permission] = [...value];
    }
  });
}

function updatePermissions(currentPermission: AdminPermissionKey) {
  const settings = requiresForConfig[currentPermission];
  if (settings !== undefined && permissionIsDeny(currentPermission)) {
    const snapshot = getPermissionsSnapshot(pvPermissions.value, settings);
    permissionSnapshots.value = {
      ...permissionSnapshots.value,
      [currentPermission]: snapshot,
    };
    Object.assign(pvPermissions.value, getPermissionsObject(settings));

    const resetFields = resetFieldsConfig[currentPermission];
    if (resetFields) {
      Object.assign(pvPermissions.value, resetFields);
    }
  } else {
    const snapshot = sortPermissionsSnapshot<TeamPermissionsState>(
      permissionSnapshots.value[currentPermission] ?? {},
      pvPermissions.value,
    );
    applyPermissionsSnapshot(pvPermissions.value, snapshot);

    if (settings) {
      const defaults = getPermissionsObject(settings);
      settings.forEach((key) => {
        if (pvPermissions.value[key] === undefined) {
          pvPermissions.value[key] = defaults[key] !== undefined
            ? defaults[key]
            : [];
        }
      });
    }

    if (currentPermission === 'offers_edit') {
      pvPermissions.value.offers_link_to_catalog_categories_allowed_list = [];
      pvPermissions.value.offers_link_to_catalog_categories_allowed_mode = 'allow_all';
    }
  }
}

function getPermissionDepends(currentPermission: AdminPermissionKey) {
  return dependsOnConfig[currentPermission] ?? [];
}

function permissionIsDisabled(currentPermission: AdminPermissionKey) {
  return getPermissionDepends(currentPermission).some(
    (dependency) => pvPermissions.value[dependency] === 'deny',
  );
}

function getPermissionSwitchProps(permission: AdminPermissionKey) {
  const value = pvPermissions.value[permission];

  return {
    class: 'team-permissions__field',
    permission,
    value: value === 'allow' || value === 'deny' ? value : undefined,
    dependencies: getPermissionDepends(permission).map((dependency) => ({
      name: dependency,
      selected: pvPermissions.value[dependency] === 'allow',
    })),
    disabled: permissionIsDisabled(permission),
    switchClass: 't:px-8',
    onChange: handlePermissionChange,
  };
}

function handlePermissionChange(permission: AdminPermissionKey, value: 'allow' | 'deny') {
  const settings = requiresForConfig[permission];
  const checkboxBackup = value === 'allow' && settings
    ? getPermissionsSnapshot(pvPermissions.value, settings)
    : null;

  pvPermissions.value[permission] = value;
  updatePermissions(permission);

  if (checkboxBackup) {
    nextTick(() => restoreClearedCheckboxFields(checkboxBackup));
  }
}
//#endregion

//#region watch
watch(
  () => props.permissions,
  (value) => {
    getUsers();
    getCurrencies();
    pvPermissions.value = cloneDeepWith(value);
  },
  { immediate: true },
);

watch(() => pvPermissions.value.crm_segments_view, () => {
  checkWeight('crm_segments_view', 'crm_segments_edit');
});

watch(() => pvPermissions.value.crm_segments_edit, () => {
  checkWeight('crm_segments_edit', 'crm_segments_review');
});

watch(() => pvPermissions.value.crm_activities_view, () => {
  checkWeight('crm_activities_view', 'crm_activities_edit');
  checkWeight('crm_activities_view', 'crm_activities_run');
  checkWeight('crm_activities_view', 'crm_activities_review');
});
//#endregion

//#region hooks
onMounted(() => {
  getCategories();
});
//#endregion
</script>


<template>
  <div
    v-loading="isLoading || isCategoriesLoading"
    class="team-permissions t:pt-16"
  >
    <ElCollapse v-model="activePermissions">
      <ElCollapseItem
        :title="t(`permissions.reports.title`)"
        name="reports"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.reports.reports_card_title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view')" />
              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.reports_affiliates_types"
                  :disabled="pvPermissions.reports_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.reports_view_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.reports_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.reports_affiliates_types"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.reports.reports_group_by') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_affiliates')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_countries')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_sub_affiliates')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_payments')" />
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.reports.reports_view_columns') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_revenue_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_profit_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_deposits_sum_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_deposits_count_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_withdrawals_sum_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_withdrawals_count_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_sport_bets_sum_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_sport_bets_count_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_casino_bets_sum_columns')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_casino_bets_count_columns')" />
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.affiliates.title`)"
        name="affiliates"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.affiliates.affiliates_card_title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_view')" />
              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.affiliates_types_view"
                  :disabled="pvPermissions.affiliates_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.affiliates_view_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.affiliates_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.affiliates_types_view"
                  />
                </UiCheckboxGroup>
              </div>

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_edit')" />

              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.affiliates_types_edit"
                  :disabled="pvPermissions.affiliates_edit === 'deny' || pvPermissions.affiliates_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.affiliates_edit_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.affiliates_edit === 'deny' || pvPermissions.affiliates_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.affiliates_types_edit"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.affiliates.affiliates_sub_affiliates`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_sub_affiliates_edit_settings')" />

              <div class="team-permissions__field t:!py-4">
                <span class="team-permissions__label">{{ t(`permissions.affiliates.affiliates_sub_affiliates_max_percent`) }}</span>
                <div class="team-permissions__values t:flex">
                  <UiInput
                    v-model="pvPermissions.affiliates_sub_affiliates_max_percent"
                    :disabled="pvPermissions.affiliates_view === 'deny' || pvPermissions.affiliates_sub_affiliates_edit_settings === 'deny' || pvPermissions.affiliates_edit === 'deny'"
                    :error="errors.affiliates_sub_affiliates_max_percent"
                    error-tooltip
                    width="80px"
                    post-text="%"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.affiliates.affiliates_actions_title') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_activity_log_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_login')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_payment_info_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_domain_change_notifications_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_reporting_settings_rs_profit_visibility_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_reporting_settings_cpa_profit_visibility_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_email_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_payments_can_create')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_external_payouts_actions')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_media_buying_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_promo_codes_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_balance_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_rotation_groups_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_limits_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_traffic_block_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_kyc_request')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_kyc_skip')" />
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.offers.offers_catalog_card_title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl
                v-bind="getPermissionSwitchProps('offers_catalog_edit')"
              />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.offers.offers_card_title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_edit')" />
              <PermissionTreeCommon
                v-if="!isCategoriesLoading"
                v-model:mode="pvPermissions.offers_link_to_catalog_categories_allowed_mode"
                v-model:ids="pvPermissions.offers_link_to_catalog_categories_allowed_list"
                class="team-permissions__field"
                :label="t('permissions.offers.use_from_catalog')"
                :disabled="pvPermissions.offers_edit === 'deny'"
                :select-placeholder="t('permissions.offers.select_categories')"
                :field-list="dataCategories"
                :permission-types="['allow_all', 'allow_selected', 'deny_selected']"
                @update:mode="onOffersCatalogModeChange"
                @update:ids="onOffersCatalogIdsChange"
              />
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_delete')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.offers.offers_revshare`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_revshare_edit_settings')" />

              <div class="team-permissions__field t:!py-4">
                <span class="team-permissions__label">{{ t(`permissions.offers.offers_revshare_max_percent_sport`) }}</span>
                <div class="team-permissions__values t:flex">
                  <UiInput
                    v-model="pvPermissions.offers_revshare_max_percent_sport"
                    :disabled="pvPermissions.affiliates_view === 'deny' || pvPermissions.affiliates_edit === 'deny' || pvPermissions.offers_edit === 'deny' || pvPermissions.offers_revshare_edit_settings === 'deny'"
                    :error="errors.offers_revshare_max_percent_sport"
                    error-tooltip
                    width="80px"
                    post-text="%"
                  />
                </div>
              </div>
              <div class="team-permissions__field t:!py-4">
                <span class="team-permissions__label">{{ t(`permissions.offers.offers_revshare_max_percent_casino`) }}</span>
                <div class="team-permissions__values t:flex">
                  <UiInput
                    v-model="pvPermissions.offers_revshare_max_percent_casino"
                    :disabled="pvPermissions.affiliates_view === 'deny' || pvPermissions.affiliates_edit === 'deny' || pvPermissions.offers_edit === 'deny' || pvPermissions.offers_revshare_edit_settings === 'deny'"
                    :error="errors.offers_revshare_max_percent_casino"
                    error-tooltip
                    width="80px"
                    post-text="%"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.offers.offers_cpa`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_cpa_edit_settings')" />
              <div class="team-permissions__field team-permissions__field_select t:!py-4">
                <span class="team-permissions__label">{{ t(`permissions.offers.offers_cpa_max_payout_currency`) }}</span>
                <div class="team-permissions__values t:flex">
                  <UiSelectElement
                    v-model="pvPermissions.offers_cpa_max_payout_currency_code"
                    :placeholder="t('permissions.offers.not_selected')"
                    :list="currencyOptions"
                    :disabled="pvPermissions.affiliates_view === 'deny' || pvPermissions.affiliates_edit === 'deny' || pvPermissions.offers_edit === 'deny' || pvPermissions.offers_cpa_edit_settings === 'deny'"
                    filterable
                    :width="160"
                    option-value-param="code"
                    :error="getError('offers_cpa_max_payout_currency_code')"
                  />
                </div>
              </div>
              <div class="team-permissions__field t:!py-4">
                <span class="team-permissions__label">{{ t(`permissions.offers.offers_cpa_max_payout`) }}</span>
                <div class="team-permissions__values t:flex">
                  <UiInput
                    v-model="pvPermissions.offers_cpa_max_payout"
                    :disabled="pvPermissions.affiliates_view === 'deny' || pvPermissions.affiliates_edit === 'deny' || pvPermissions.offers_edit === 'deny' || pvPermissions.offers_cpa_edit_settings === 'deny'"
                    :error="getError('offers_cpa_max_payout')"
                    error-tooltip
                    width="80px"
                  />
                </div>
              </div>
              <div class="team-permissions__field t:!py-4">
                <span class="team-permissions__label">{{ t(`permissions.offers.offers_cpa_min_payout_deposits_sum_ratio`) }}</span>
                <div class="team-permissions__values t:flex">
                  <UiInput
                    v-model="pvPermissions.offers_cpa_min_payout_deposits_sum_ratio"
                    :disabled="pvPermissions.affiliates_view === 'deny' || pvPermissions.affiliates_edit === 'deny' || pvPermissions.offers_edit === 'deny' || pvPermissions.offers_cpa_edit_settings === 'deny'"
                    :error="getError('offers_cpa_min_payout_deposits_sum_ratio')"
                    error-tooltip
                    width="80px"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.affiliates.affiliates_notes') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notes_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notes_edit')" />
            </div>
          </div>

          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.affiliates.affiliates_security_title') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_password_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_2fa_can_reset')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.affiliates.affiliates_status_change_title') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_approve')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_decline')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_block')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_unblock')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_revoke')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.affiliates.affiliates_notifications') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notifications_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notifications_edit')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.affiliates.affiliates_kpi') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_kpi_bonuses_edit')" />
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.payments.title`)"
        name="payments"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.payments.payments_card_title') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_review_actions')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_chargebacks_actions')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_make_manual_cpa_payouts')" />
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.players.title`)"
        name="players"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.players.players_card_title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('players_view')" />

              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.players_available_property_groups"
                  :disabled="pvPermissions.players_view === 'deny'"
                  :options="[
                    {
                      label: t('permissions.players.subId'),
                      value: 'sub_id',
                    },
                    {
                      label: t('permissions.players.country'),
                      value: 'country',
                    },
                    {
                      label: t('permissions.players.personal_information'),
                      value: 'personal_information',
                    },
                    {
                      label: t('permissions.players.affiliate'),
                      value: 'affiliate',
                    },
                    {
                      label: t('permissions.players.contacts'),
                      value: 'contacts',
                    },
                    {
                      label: t('permissions.players.visit'),
                      value: 'visit_information',
                    },
                  ]"
                  :columns="2"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.players.players_card_other') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('players_virtual_edit_settings')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('players_assign_affiliate')" />
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.dms.title`)"
        name="dms"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.dms.dns_hosting_accounts') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('dns_hosting_accounts_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('dns_hosting_accounts_edit')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.dms.domain_registrar_accounts') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('domain_registrar_accounts_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('domain_registrar_accounts_edit')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.dms.platform_servers') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_servers_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_servers_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_servers_can_run_jobs')" />
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.dms.platform_service_domains') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_service_domains_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_service_domains_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_service_domains_can_run_jobs')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.dms.service_rotation_groups') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_domains_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_domains_edit')" />
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t('permissions.dms.dedicated_site_domains') }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_betting_domains_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_betting_domains_edit')" />
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.media_buying.title`)"
        name="media-buying"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.media_buying.payments`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_payments_view')" />
              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_payments_affiliates_types_view"
                  :disabled="pvPermissions.media_buying_payments_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.media_buying_payments_view_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.media_buying_payments_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.media_buying_payments_affiliates_types_view"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_payments_review')" />

              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_payments_affiliates_types_review"
                  :disabled="pvPermissions.media_buying_payments_review === 'deny' || pvPermissions.media_buying_payments_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="paymentReviewTeammateIds"
                    :disabled="pvPermissions.media_buying_payments_review === 'deny' || pvPermissions.media_buying_payments_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.media_buying_payments_affiliates_types_review"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.media_buying.payments_moderation`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.media_buying.statuses`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_payments_moderation_statuses_visible"
                  :disabled="pvPermissions.media_buying_payments_view === 'deny'"
                  :options="statusList"
                  :columns="2"
                />
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.media_buying.payments_payout`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.media_buying.statuses`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_payments_payout_statuses_visible"
                  :disabled="pvPermissions.media_buying_payments_view === 'deny'"
                  :options="[
                    {
                      label: t('permissions.not_paid'),
                      value: 'not_paid',
                    },
                    {
                      label: t('permissions.paid'),
                      value: 'paid',
                    },
                    {
                      label: t('permissions.for_rework'),
                      value: 'for_rework',
                    },
                    {
                      label: t('permissions.failed'),
                      value: 'failed',
                    },
                    {
                      label: t('permissions.processing'),
                      value: 'processing',
                    },
                  ]"
                  :columns="2"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.media_buying.posts`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_posts_view')" />

              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_posts_affiliates_types_view"
                  :disabled="pvPermissions.media_buying_posts_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.media_buying_posts_view_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.media_buying_posts_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.media_buying_posts_affiliates_types_view"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_posts_review')" />

              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_posts_affiliates_types_review"
                  :disabled="pvPermissions.media_buying_posts_view === 'deny' || pvPermissions.media_buying_posts_review === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.media_buying_posts_review_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.media_buying_posts_view === 'deny' || pvPermissions.media_buying_posts_review === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.media_buying_posts_affiliates_types_review"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.media_buying.statuses`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_posts_statuses_visible"
                  :disabled="pvPermissions.media_buying_posts_view === 'deny'"
                  :options="statusList"
                  :columns="2"
                />
              </div>
            </div>
          </div>
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.reports.reports_card_title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_reports_view')" />

              <div class="team-permissions__field">
                <UiCheckboxGroup
                  v-model="pvPermissions.media_buying_reports_affiliates_types_view"
                  :disabled="pvPermissions.media_buying_reports_view === 'deny'"
                  :options="partnersTypeList"
                  :columns="2"
                >
                  <PermissionTree
                    v-model:teammates-ids="pvPermissions.media_buying_reports_view_affiliates_visibility_teammates_id"
                    :disabled="pvPermissions.media_buying_reports_view === 'deny'"
                    :users-list="users"
                    :affiliates-types="pvPermissions.media_buying_reports_affiliates_types_view"
                  />
                </UiCheckboxGroup>
              </div>
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.crm.title`)"
        name="crm"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.crm.segments`) }}
              </template>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.segments_view`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_segments_view"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.segments_edit`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_segments_edit"
                  :disabled="pvPermissions.crm_segments_view === 'deny'"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :disabled="isDisabledOption('crm_segments_view', item.value)"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.segments_review`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_segments_review"
                  :disabled="pvPermissions.crm_segments_edit === 'deny' || pvPermissions.crm_segments_view === 'deny'"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :disabled="isDisabledOption('crm_segments_edit', item.value)"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.crm.activities`) }}
              </template>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.activities_view`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_activities_view"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.activities_edit`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_activities_edit"
                  :disabled="pvPermissions.crm_activities_view === 'deny'"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :disabled="isDisabledOption('crm_activities_view', item.value)"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.activities_review`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_activities_review"
                  :disabled="pvPermissions.crm_activities_view === 'deny'"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :disabled="isDisabledOption('crm_activities_view', item.value)"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <span class="team-permissions__label">{{ t(`permissions.crm.activities_run`) }}</span>
              </div>
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.crm_activities_run"
                  :disabled="pvPermissions.crm_activities_view === 'deny'"
                  class="t:w-full"
                  :columns="2"
                >
                  <template v-slot:buttons>
                    <UiRadioButton
                      v-for="(item, id) in crmRadioList"
                      :key="id"
                      :disabled="isDisabledOption('crm_activities_view', item.value)"
                      :value="item.value"
                      :text="item.label"
                    />
                  </template>
                </UiRadioGroup>
              </div>
            </div>
          </div>
        </div>
      </ElCollapseItem>
      <ElCollapseItem
        :title="t(`permissions.others.title`)"
        name="others"
      >
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.tools.title`) }}
              </template>
              <span>
                <span class="team-permissions__status-label">
                  {{ t('permissions.status') }}
                </span>
              </span>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <PermissionSwitchControl v-bind="getPermissionSwitchProps('tools_promo_landings_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('tools_promo_landings_edit')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('marketing_materials_view')" />

              <PermissionSwitchControl v-bind="getPermissionSwitchProps('marketing_materials_edit')" />
            </div>
          </div>
        </div>
        <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
          <div class="team-permissions__card t:mb-16 t:w-full">
            <CardTitle>
              <template v-slot:title>
                {{ t(`permissions.geo.geo_card_title`) }}
              </template>
            </CardTitle>
            <div class="team-permissions__card-content t:px-8">
              <div class="team-permissions__field">
                <UiRadioGroup
                  v-model="pvPermissions.countries_allowed_mode"
                  :options="[
                    {
                      label: t('permissions.allow_all'),
                      value: 'allow_all',
                    },
                    {
                      label: t('permissions.allow_selected'),
                      value: 'allow_selected',
                    },
                    {
                      label: t('permissions.deny_all'),
                      value: 'deny_all',
                    },
                    {
                      label: t('permissions.deny_selected'),
                      value: 'deny_selected',
                    }
                  ]"
                  class="t:w-full"
                  :columns="2"
                  @change="checkGeoPermission(pvPermissions.countries_allowed_mode)"
                >
                  <UiSelectElement
                    v-model="pvPermissions.countries_allowed_list"
                    :placeholder="t(`permissions.add_country`)"
                    :list="countries"
                    size="big"
                    search-param="name"
                    option-value-param="code"
                    filterable
                    multiple
                    collapse-tag-tooltip
                    :max-collapse-tags="1"
                    :error="errors.countries_allowed_list"
                    :disabled="pvPermissions.countries_allowed_mode === 'allow_all' || pvPermissions.countries_allowed_mode === 'deny_all'"
                  />
                </UiRadioGroup>
              </div>
            </div>
          </div>
        </div>
      </ElCollapseItem>
    </ElCollapse>
    <div
      v-if="isEdited"
      class="team-permissions__save-bar t:flex t:items-center"
    >
      <div class="content-wrapper t:flex t:items-center t:justify-end t:space-x-10">
        <div>
          {{ t('permissions.save_changes', { teamName: props.teamInfo.name }) }}
        </div>
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="cancelEdit"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="postTeamPermissions"
        >
          {{ t('team_popup.save_button') }}
        </UiButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.team-permissions{
  &__save-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 5;
    width: 100%;
    height: 60px;
    border: 1px solid #eaeaea;
    background: #eef5f3;
    box-shadow: 0 0 16px 0 rgba(32, 129, 94, .32);
  }

  &__field {
    display: flex;
    flex-grow: 1;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 14px;
    line-height: 20px;

    & + & {
      border-top: 1px solid var(--border-divider);
    }
  }

  &__status-label {
    display: inline-block;
    width: 56px;
    padding: 0 8px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 400;
    text-align: center;
  }

  .el-collapse{
    border: none;
    &-item{
      margin-bottom: 16px;
      width: 100%;
      &__header,
      &__wrap{
        border-bottom: none;
      }
      &__header{
        padding: 12px 16px;
        height: 48px;
        font-size: 20px;
        color: var(--text-body);
        border-radius: 8px;
        background: var(--background-surface);
        font-weight: normal;
        width: auto;
        min-height: 20px;
        &.focusing:focus:not(:hover),
        &:focus {
          color: var(--text-body);
        }
        &:hover{
          background-color: var(--background-brand-subtle);
        }
      }
      &__wrap{
        margin-top: 16px;
      }
      &__content{
        padding-bottom: 0;
        display: flex;
        width: 100%;
        gap: 30px;
      }
    }
  }
}
</style>
